import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PaymentWebviewModal from './PaymentWebviewModal';

interface PaymentDetailsProps {
  paymentUrl: string;
  price: number;
  expiryTime: Date | null;
  onPayNow?: () => void;
  autoOpen?: boolean;
  onAutoOpened?: () => void;
  onPaymentDone?: () => void;
}

const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  paymentUrl,
  price,
  expiryTime,
  onPayNow,
  autoOpen,
  onAutoOpened,
  onPaymentDone,
}) => {
  const { t } = useTranslation('payment_details');
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);
  const [showWebview, setShowWebview] = useState(false);

  useEffect(() => {
    if (!expiryTime) return;

    const updateTimer = () => {
      const now = new Date();
      const diff = expiryTime.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining(t('expired'));
        setIsExpired(true);
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [expiryTime]);

  // Auto-open webview after checkout
  useEffect(() => {
    if (autoOpen && paymentUrl && !showWebview) {
      setShowWebview(true);
      onAutoOpened?.();
    }
  }, [autoOpen, paymentUrl]);

  const formatDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    return date.toLocaleDateString('en-GB', options).replace(',', '');
  };

  const handlePayClick = () => {
    if (onPayNow) {
      onPayNow();
    } else {
      setShowWebview(true);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
      {/* Payment Expiry Row - only show when we have expiry */}
      {expiryTime && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <Clock className="text-primary flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-sm font-medium text-gray-700">{t('pay_before')}</h3>
                <p className="text-base md:text-lg font-semibold text-gray-900">
                  {formatDate(expiryTime)}
                </p>
              </div>
            </div>

            <div className="text-right">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 rounded-lg">
                <Clock className="text-red-600" size={20} />
                <span className="text-xl font-bold text-red-600">{timeRemaining}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200"></div>
        </>
      )}

      {/* Payment Amount */}
      <div className="text-center space-y-1">
        <p className="text-sm text-gray-500">{t('total_amount')}</p>
        <p className="text-3xl font-bold text-gray-900">USD {price.toFixed(2)}</p>
      </div>

      {/* Pay via Mosan Button */}
      <div className="flex flex-col items-center space-y-3">
        <div className="text-xl font-bold text-primary">MOSAN</div>
        <p className="text-sm text-gray-600 text-center">
          {t('click_pay_via_mosan')}
        </p>
        <button
          onClick={handlePayClick}
          disabled={isExpired}
          className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg text-white font-semibold transition-colors ${
            isExpired
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary hover:brightness-90'
          }`}
        >
          {t('pay_via_mosan')}
        </button>
        {!isExpired && (
          <p className="text-xs text-gray-400 text-center">
            {t('after_payment_hint')}
          </p>
        )}
      </div>

      {showWebview && paymentUrl && (
        <PaymentWebviewModal
          url={paymentUrl}
          onClose={() => setShowWebview(false)}
          onPaymentDone={onPaymentDone}
        />
      )}
    </div>
  );
};

export default PaymentDetails;
